import {
  ActionList,
  ActionListItem,
  Button,
  Dropdown,
  DropdownOverlay,
  PlusIcon,
  SelectInput,
  TextInput,
} from '@razorpay/blade/components';
import closeIcon from 'assets/icon--close-grey.svg';
import {
  DeductFrom,
  DeductFromLabelToNumberMap,
  DeductionComponentV2,
  DeductionType,
} from 'components/RunPayroll/utils/types';
import { useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { DeductionsComponentType } from 'components/SalaryComponents/types';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import { SecondaryButton } from 'components/ui/Button';
import { Table } from 'components/ui/Table';
import React, { useState } from 'react';
import { capitalizeAllWordsInString } from 'utils/Strings';
import { getStaticMediaUrl } from 'utils/Urls';
import crossIcon from './../../../assets/icons/red_cross.png';
import useSalaryComponentsList from './EditSalary/hooks/useSalaryComponentsList';
import {
  getDeductionAdhocPayments,
  getDeductionRecurring,
  getSearchDropdownDescription,
  isDeductionAdhocPayment,
  isDeductionRecurring,
} from './EditSalary/utils';
import styles from './index.module.scss';
import { isNumeric, uniqueId } from 'utils/Numbers';
import { overlayLayers } from 'constants/overlayLayers.generated';

const EditDeductionsV2 = ({
  deductions,
  setNonLOPDeductions,
}: {
  deductions: DeductionComponentV2;
  setNonLOPDeductions: (nonLOPDeductions: DeductionComponentV2['non_lop_deductions']) => void;
}) => {
  const { openDeductionsCreation } = useSalaryComponentHelpers();
  const [rawAmountInputs, setRawAmountInputs] = useState<Record<string, string>>({});

  const { searchSalaryComponents, isFetchingSalaryComponents, getSalaryComponentById } =
    useSalaryComponentsList<DeductionsComponentType>({
      requestParams: {
        category: 'DEDUCTIONS_CATEGORY',
        payType: 'LIST_ADHOC_TYPE',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });

  const adhocPaymentIds = getDeductionAdhocPayments(deductions).map((e) => e.id);
  const recurringDeductionIds = getDeductionRecurring(deductions).map((e) => e.id);

  const onDeductionsChange = (
    updateId: string,
    updates: Partial<DeductionComponentV2['non_lop_deductions'][0]>,
  ) => {
    const nonLopDeductions = [...deductions.non_lop_deductions];
    const indexToUpdate = nonLopDeductions.findIndex((deduction) => deduction.id === updateId);

    if (indexToUpdate !== -1) {
      const componentId = updates.id ? updates.id : nonLopDeductions[indexToUpdate].id;
      const selectedComponent = getSalaryComponentById(componentId);

      nonLopDeductions[indexToUpdate] = {
        ...nonLopDeductions[indexToUpdate],
        ...updates,
        deduct_from:
          DeductFromLabelToNumberMap[
            selectedComponent?.settings.deductionDetails.deductFrom ?? 'GROSS_PAY'
          ],
      };

      setNonLOPDeductions(nonLopDeductions);
    }
  };

  const onAdd = () => {
    const nonLOPDeductions = [...deductions.non_lop_deductions];
    nonLOPDeductions.push({
      id: uniqueId(),
      name: '',
      amount: 0,
      deduct_from: DeductFrom.GROSS_PAY,
      lop_days: '0',
      type: DeductionType.TYPE_MANUAL,
      label: '',
    });
    setNonLOPDeductions(nonLOPDeductions);
  };

  const onDelete = (id: string) => {
    const nonLOPDeductions = deductions.non_lop_deductions.filter(
      (deduction) => deduction.id !== id,
    );
    setNonLOPDeductions(nonLOPDeductions);
  };

  const onHoverCross = (idx: number) => {
    const row = document.getElementById(`deduction-row-${idx}`);
    row?.classList.add(styles['run-payroll-deduction-row-hover']);
  };

  const onMouseoutOfCross = (idx: number) => {
    const row = document.getElementById(`deduction-row-${idx}`);
    row?.classList.remove(styles['run-payroll-deduction-row-hover']);
  };

  const getDeductionsSearchSkipList = (
    deductions: DeductionComponentV2['non_lop_deductions'],
    currentId: string,
  ): string[] => {
    return deductions
      .filter((deduction) => deduction.id !== '' && deduction.id !== currentId)
      .map((deduction) => deduction.id);
  };

  const shouldShowAsDisabledItem = (item: DeductionComponentV2['non_lop_deductions'][0]) => {
    return isDeductionAdhocPayment(item) || isDeductionRecurring(item);
  };

  return (
    <>
      <Table
        className={
          styles['run-payroll-edit-deduction-table'] +
          ' border border-solid border-surfaceBorderGrayMuted'
        }
        showAlternateColors={false}>
        <thead>
          <tr>
            <th className={styles['run-payroll-edit-model-deduction-label-column']}>Label</th>
            <th>
              <a
                href="https://intercom.help/XPayroll/en/articles/8512408-deductions-loss-of-pay-days"
                target="_blank"
                rel="noreferrer">
                Deduct From?
              </a>
            </th>
            <th className={styles['run-payroll-edit-model-deduction-amount-column']}>Amount</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {deductions.non_lop_deductions.map((deductionItem, idx) => {
            return (
              <tr key={idx} id={`deduction-row-${idx}`}>
                <td>
                  {shouldShowAsDisabledItem(deductionItem) ? (
                    <TextInput label="" value={`${deductionItem.name} *`} isDisabled />
                  ) : (
                    <AutoCompleteV2
                      options={searchSalaryComponents([
                        ...adhocPaymentIds,
                        ...recurringDeductionIds,
                        ...getDeductionsSearchSkipList(
                          deductions.non_lop_deductions,
                          deductionItem.id,
                        ),
                      ])}
                      itemToKey={(item) => item.id}
                      itemToLabel={(item) => capitalizeAllWordsInString(item.name)}
                      itemToDescription={(item) => getSearchDropdownDescription(item) ?? ''}
                      data-testid={`deduction-${deductionItem.name}-name`}
                      value={deductionItem.id}
                      placeholder="Eg. Laptop recovery"
                      isDisabled={isFetchingSalaryComponents}
                      onChange={(event) => {
                        onDeductionsChange(deductionItem.id, {
                          name: event.name,
                          id: event.id,
                        });
                      }}
                      onClearSelection={() => {
                        onDeductionsChange(deductionItem.id, {
                          name: '',
                          id: '',
                        });
                      }}
                      footer={
                        <Button
                          variant="tertiary"
                          size="small"
                          icon={PlusIcon}
                          onClick={openDeductionsCreation}
                          isFullWidth>
                          Create deduction
                        </Button>
                      }
                    />
                  )}
                </td>
                <td>
                  <Dropdown>
                    <SelectInput
                      name="deduct-from"
                      value={deductionItem?.deduct_from?.toString() ?? ''}
                      label=""
                      isDisabled
                    />
                    <DropdownOverlay zIndex={overlayLayers.nested}>
                      <ActionList>
                        <ActionListItem
                          key={'gross-pay'}
                          title={'Gross Pay'}
                          value={DeductFrom.GROSS_PAY.toString()}
                        />
                        <ActionListItem
                          key={'net-pay'}
                          title={'Net Pay'}
                          value={DeductFrom.NET_PAY.toString()}
                        />
                      </ActionList>
                    </DropdownOverlay>
                  </Dropdown>
                </td>
                <td>
                  <TextInput
                    label=""
                    isDisabled={shouldShowAsDisabledItem(deductionItem)}
                    type="text"
                    testID={`deduction-${deductionItem.name}-value`}
                    value={rawAmountInputs[deductionItem.id] ?? (deductionItem?.amount !== 0 ? deductionItem?.amount.toString() : '')}
                    onChange={(e) => {
                      const val = e.value ?? '';
                      if (val === '' || val === '-' || /^-?\d+$/.test(val)) {
                        setRawAmountInputs((prev) => ({ ...prev, [deductionItem.id]: val }));
                        if (val !== '' && val !== '-') {
                          onDeductionsChange(deductionItem.id, { amount: Number(val) });
                        }
                      }
                    }}
                    onBlur={() => {
                      const raw = rawAmountInputs[deductionItem.id];
                      if (raw === '-' || raw === '') {
                        onDeductionsChange(deductionItem.id, { amount: 0 });
                      }
                      setRawAmountInputs((prev) => {
                        const next = { ...prev };
                        delete next[deductionItem.id];
                        return next;
                      });
                    }}
                  />
                </td>
                <td
                  onMouseOver={() =>
                    shouldShowAsDisabledItem(deductionItem) ? null : onHoverCross(idx)
                  }
                  onMouseOut={() =>
                    shouldShowAsDisabledItem(deductionItem) ? null : onMouseoutOfCross(idx)
                  }>
                  {shouldShowAsDisabledItem(deductionItem) ? null : (
                    <>
                      <img
                        className={styles['run-payroll-deduction-red-cross']}
                        src={getStaticMediaUrl(crossIcon)}
                        alt=""
                        onClick={() => onDelete(deductionItem.id)}
                      />
                      <img
                        className={styles['run-payroll-deduction-grey-close']}
                        src={getStaticMediaUrl(closeIcon)}
                        alt=""
                        onClick={() => onDelete(deductionItem.id)}
                      />
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {deductions.non_lop_deductions.length < 10 && (
        <SecondaryButton
          onClick={onAdd}
          disabled={false}
          className={styles['run-payroll-new-deduction-button']}
          type="button">
          + New deduction
        </SecondaryButton>
      )}
    </>
  );
};

export default EditDeductionsV2;
